'use client'

import { useNavbarHeightPx } from '@components/common/Navbar/Navbar.utils'
import { stringToHash } from '@utils/contentful/stringToHash'
import { useDevice } from '@utils/responsiveness/useDevice'
import { useWindowSize } from '@utils/responsiveness/useWindowSize'
import dynamic from 'next/dynamic'
import { useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { IAnchorTag } from 'types/anchor'
import { useMergeRefs } from 'use-callback-ref'
import { TContentfulComponents } from 'types/contentful/contentfulComponents'
import { useScrollPosition } from '@utils/responsiveness/useScrollPosition'
import { ContentfulPage } from 'app/_components/ContentfulPage/ContentfulPage'

const GALLERY_SECTION_LABEL = 'Gallery'

const DynamicProductPageSubmenu = dynamic(() => import('../ProductPageSubmenu'))

export function TopProductFeaturesClient({
  sectionComponents,
  contentfulComponentsAnchors,
}: {
  sectionComponents: TContentfulComponents
  contentfulComponentsAnchors: IAnchorTag[]
}) {
  const { isTablet, isMobile } = useDevice({
    mobileMediaQuery: 480,
    tabletMediaQuery: 1279,
  })
  const { primaryMenuHeightPx, secondaryMenuHeightPx } = useNavbarHeightPx()

  const [hasScrolled, setHasScrolled] = useState(false)

  const [scrolledHash, setScrolledHash] = useState(
    stringToHash(GALLERY_SECTION_LABEL)
  )

  useScrollPosition(
    ({ prevPos, currPos }) => {
      const scrolled = currPos.y < prevPos.y
      if (scrolled !== hasScrolled) {
        setHasScrolled(scrolled)
      }
    },
    [hasScrolled]
  )

  const updateScrolledHash = (label) => {
    setScrolledHash(stringToHash(label))
  }

  const subMenuRef = useRef(null)

  const { ref: subMenuInViewRef, inView: subMenuInView } = useInView({
    triggerOnce: true,
    rootMargin: '100px 0px',
  })

  const { height, screenHeight } = useWindowSize()

  const customComponentsAnchors: IAnchorTag[] = [
    { label: GALLERY_SECTION_LABEL, hash: stringToHash(GALLERY_SECTION_LABEL) },
  ]
  const submenuItems: IAnchorTag[] = [
    ...customComponentsAnchors,
    ...contentfulComponentsAnchors,
  ]

  useEffect(() => {
    const handleWindowScroll = () => {
      const galleryMiddleLineOffset = (height || screenHeight) / 2
      const subMenuElement = subMenuRef?.current
      const subMenuTopOffset = subMenuElement?.getBoundingClientRect()

      if (subMenuTopOffset?.top >= galleryMiddleLineOffset) {
        setScrolledHash(stringToHash(GALLERY_SECTION_LABEL))
      } else {
        setScrolledHash(submenuItems?.[1]?.hash)
      }
    }

    window.addEventListener('scroll', handleWindowScroll)
    return () => {
      window.removeEventListener('scroll', handleWindowScroll)
    }
  }, [scrolledHash])

  return (
    <>
      <div
        ref={useMergeRefs([subMenuRef, subMenuInViewRef])}
        className={`sticky col-span-1 col-start-1 overflow-hidden transition-all z-10 duration-200 ease-linear top-0`}
        style={{
          top: hasScrolled
            ? isMobile || isTablet
              ? '0px'
              : `${primaryMenuHeightPx}px`
            : `${primaryMenuHeightPx + secondaryMenuHeightPx}px`,
        }}
      >
        {submenuItems?.length && subMenuInView ? (
          <DynamicProductPageSubmenu
            submenuItems={submenuItems}
            hashFromScrollPosition={scrolledHash}
          />
        ) : null}
      </div>

      <div className="col-span-1 col-start-1 lg:overflow-hidden">
        <ContentfulPage
          componentsProps={sectionComponents}
          onScrollProgress={updateScrolledHash}
        />
      </div>
    </>
  )
}
